import type { PromoCampaignModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
	CheckIcon,
	CircleDollarSignIcon,
	InfoIcon,
	Trash2Icon,
	Undo2Icon,
	XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { UsernameById } from '@/entities/user'

import DocumentTitle from '@/shared/components/DocumentTitle'
import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Blanket } from '@/shared/components/ui/blanket'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { DateFormat } from '@/shared/tools/dates'

import { usePromoCampaignForm } from '../lib/usePromoCampaignForm'
import PromoCampaignFormFields from './PromoCampaignFormFields'
import PromoCampaignProducts from './PromoCampaignProducts'
import PromoCampaignProductsImport from './PromoCampaignProductsImport'

export default function PromoCampaignForm({
	promoCampaign,
	options,
	isNew,
}: {
	promoCampaign?: PromoCampaignModel
	options: Record<string, TreeNodeType[]>
	isNew?: boolean
}) {
	const { t } = useTranslation()

	const [isImportShowing, setIsImportShowing] = useState(false)
	const mode = useMemo(() => (isNew ? 'create' : 'update'), [isNew])
	const onFileUploaded = () => setIsImportShowing(false)
	const { isLoading, methods, onSubmit, handleDelete, handleFileUploaded } =
		usePromoCampaignForm({
			promoCampaign,
			onFileUploaded,
			mode,
		})
	const handleSave = methods.handleSubmit(onSubmit)

	return (
		<DocumentTitle title={t('promo_title_page')}>
			<Layout>
				<Header className='h-11 items-center px-4 py-2'>
					<CircleDollarSignIcon className='size-4' />
					<Separator orientation='vertical' className='mx-2 h-5' />
					<Breadcrumb className='text-sm'>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<Link
										to='/p/promo'
										data-test-id='breadcrumb-link'
										className='max-w-32 truncate'
									>
										{t('menu.promo')}
									</Link>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{promoCampaign?.name ?? 'New'}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
					<div className='ml-auto flex items-center gap-2'>
						{mode === 'update' && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button key='delete' size='icon' onClick={handleDelete}>
										<Trash2Icon />
									</Button>
								</TooltipTrigger>
								<TooltipContent>{t('general_delete')}</TooltipContent>
							</Tooltip>
						)}
						{methods.formState.isDirty && mode === 'update' && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button onClick={() => methods.reset()} size='icon'>
										<Undo2Icon />
									</Button>
								</TooltipTrigger>
								<TooltipContent>{t('app.revert_changes')}</TooltipContent>
							</Tooltip>
						)}

						<Button
							variant='primary'
							onClick={handleSave}
							disabled={!methods.formState.isDirty}
						>
							<CheckIcon />
							{mode === 'create' && t('general_save_and_create')}
							{mode === 'update' && t('general_save')}
						</Button>
					</div>
				</Header>

				<Layout className='relative h-full overflow-hidden border-t'>
					<Content id='promo-content' className='overflow-hidden'>
						<Blanket
							className='bg-backdrop blanket-abs blanket-products'
							isTinted={isLoading}
						>
							<Loader />
						</Blanket>
						<div className='rounded-0 flex size-full flex-col overflow-hidden'>
							<FormProvider
								methods={methods}
								onSubmit={methods.handleSubmit(onSubmit)}
								className='h-full'
							>
								<div className='bg-default-background flex size-full flex-col items-start overflow-hidden'>
									<div className='flex size-full w-full shrink-0 grow basis-0 flex-wrap items-start'>
										<div className='flex h-full w-80 flex-none flex-col items-start justify-between self-stretch border-r border-solid'>
											<div className='flex size-full flex-col gap-px overflow-y-auto border-b border-solid'>
												<Header className='h-10 items-center justify-between px-4 py-1'>
													<h3 className='text-sm font-bold'>
														General settings
													</h3>
													{!!promoCampaign && (
														<section>
															<div className='mt-1 capitalize'>
																<Badge>{promoCampaign?.source}</Badge>
															</div>
														</section>
													)}
												</Header>
												<div className='grid gap-3 border-b px-4 py-2'>
													<PromoCampaignFormFields options={options} />
												</div>
												<div className='mt-auto flex flex-col gap-3 border-b px-4 py-2'>
													{promoCampaign?.user_created && (
														<section>
															<span className='text-sm font-medium text-muted-foreground'>
																{t('general_created')}:
															</span>
															<p className='text-sm font-medium'>
																by{' '}
																<UsernameById
																	userId={promoCampaign.user_created}
																/>
																,{' '}
																{promoCampaign.created_on &&
																	format(
																		new Date(promoCampaign.created_on),
																		DateFormat.system
																	)}
															</p>
														</section>
													)}
													{promoCampaign?.user_modified && (
														<section>
															<span className='text-sm font-medium text-muted-foreground'>
																{t('general_last_updated')}:
															</span>
															<p className='text-sm font-medium'>
																by{' '}
																<UsernameById
																	userId={promoCampaign.user_modified}
																/>
																,{' '}
																{promoCampaign.modified_on &&
																	format(
																		new Date(promoCampaign.modified_on),
																		DateFormat.system
																	)}
															</p>
														</section>
													)}
												</div>
											</div>
										</div>
										<div className='relative flex size-full shrink-0 grow basis-0 flex-col gap-2 self-stretch overflow-hidden'>
											{mode === 'update' && (
												<ErrorBoundaryWithUser showDialog>
													<Header className='h-10 items-center justify-between border-b px-4 py-1'>
														<h3 className='text-sm font-bold'>
															{t('app.products')}
														</h3>
														{isImportShowing ? (
															<Button
																size='icon-sm'
																onClick={() => setIsImportShowing(false)}
															>
																<XIcon />
															</Button>
														) : (
															<Button
																size='sm'
																onClick={() => setIsImportShowing(true)}
															>
																{t('app.import_from_file')}
															</Button>
														)}
													</Header>
													{isImportShowing ? (
														<PromoCampaignProductsImport
															id={promoCampaign!.id!}
															onDone={handleFileUploaded}
														/>
													) : (
														<PromoCampaignProducts campaign={promoCampaign!} />
													)}
												</ErrorBoundaryWithUser>
											)}
											{mode === 'create' && (
												<Empty className='flex h-full flex-col items-center justify-center'>
													<EmptyHeader>
														<EmptyMedia variant='icon'>
															<InfoIcon className='text-muted-foreground' />
														</EmptyMedia>
														<EmptyTitle>
															You must create a campaign first.
														</EmptyTitle>
														<EmptyDescription>
															Save it and then you can add products.
														</EmptyDescription>
													</EmptyHeader>
												</Empty>
											)}
										</div>
									</div>
								</div>
							</FormProvider>
						</div>
					</Content>
				</Layout>
			</Layout>
		</DocumentTitle>
	)
}
